/**
 * Trunnion AI - Approval Queue Widget
 * Compact pending approvals card for the dashboard.
 */

import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { useNotificationStore } from '@/stores/notificationStore';
import { ROUTES } from '@/config/routes';

function timeRemaining(expiresAt: string): string {
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  const diff = expiry - now;

  if (diff <= 0) return 'Expired';

  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) return `${hours}h ${minutes % 60}m remaining`;
  return `${minutes}m remaining`;
}

export function ApprovalQueueWidget() {
  const navigate = useNavigate();
  const approvalQueue = useNotificationStore((s) => s.approvalQueue);
  const count = approvalQueue.length;

  return (
    <Card
      padding="none"
      className={
        count > 0
          ? 'border-amber-500/30'
          : ''
      }
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <Clock className="h-4 w-4 inline mr-1.5 text-amber-400" />
            Pending Approvals
          </CardTitle>
          {count > 0 && (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-amber-500/20 px-1.5 text-xs font-bold text-amber-400">
              {count}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {count === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 gap-2">
            <CheckCircle2 className="h-8 w-8 text-green-400/60" />
            <p className="text-sm text-trunnion-slate-400">No pending approvals</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Prominent count */}
            <div className="text-center">
              <p className="text-3xl font-bold text-amber-400">{count}</p>
              <p className="text-xs text-trunnion-slate-400">
                {count === 1 ? 'approval waiting' : 'approvals waiting'}
              </p>
            </div>

            {/* Up to 3 items */}
            <div className="space-y-2">
              {approvalQueue.slice(0, 3).map((item) => (
                <div
                  key={item.id}
                  className="rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/30 p-2.5"
                >
                  <p className="text-xs text-trunnion-slate-200 truncate">
                    {item.description}
                  </p>
                  <p className="text-[10px] text-amber-400/80 mt-1">
                    {timeRemaining(item.expiresAt)}
                  </p>
                </div>
              ))}
            </div>

            {/* View all link */}
            <button
              onClick={() => navigate(ROUTES.EXECUTIONS)}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/30 px-3 py-2 text-xs font-medium text-trunnion-slate-300 transition-colors hover:bg-trunnion-slate-800 hover:text-white"
            >
              View All
              <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
